/**
 * API client for the Budget Widget
 */

import { getLang } from '../i18n/lang';
import type { BudgetData, FilterOptions, FilterState, SearchSuggestion, ExpenditureTypeData, BudgetEntriesResponse, TitleData, TitleDetailsResponse, ExportResponse, BudgetTotalData, FilterMatrixResponse, SonstigesTitle } from '../types';

export class BudgetApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  private buildUrl(endpoint: string, params?: Record<string, string | undefined>): string {
    // Build the base path
    let url = `${this.baseUrl}${endpoint}`;

    // Add query parameters
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        // Allow empty string for subType parameter (needed for filtering sub_expenditure_type = '')
        if (value !== undefined && value !== null && (value !== '' || key === 'subType')) {
          searchParams.append(key, value);
        }
      });
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return url;
  }

  /**
   * Fire a one-shot, fire-and-forget page-view beacon so we can count widget
   * loads exactly (Railway egress can only approximate it). Uses sendBeacon so
   * it survives even if the page navigates away mid-mount; falls back to a
   * keepalive fetch. The widget name and UI language ride in the query string
   * (no JSON body → no CORS preflight), and the server answers 204. Never
   * throws. Data requests carry no language: the budget data is German-only.
   */
  trackPageview(): void {
    const url = `${this.baseUrl}/analytics/pageview?w=budget&lang=${getLang()}`;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(url);
        return;
      }
      void fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
    } catch {
      // Analytics must never affect the widget.
    }
  }

  private async fetch<T>(endpoint: string, params?: Record<string, string | undefined>): Promise<T> {
    const url = this.buildUrl(endpoint, params);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return (await response.json()) as T;
  }

  /**
   * Fetch budget data for charts
   */
  async getBudgetData(filters: FilterState, titleId?: string): Promise<BudgetData> {
    return this.fetch<BudgetData>('/budget', {
      type: filters.type,
      ep: filters.einzelplan || undefined,
      kap: filters.kapitel || undefined,
      q: filters.query || undefined,
      expType: filters.expenditureType || undefined,
      subExpType: filters.subExpType || undefined,
      titleId: titleId || undefined,
      funktion: filters.funktion || undefined,
      oberfunktion: filters.oberfunktion || undefined,
      hauptfunktion: filters.hauptfunktion || undefined,
      gruppierung: filters.gruppierung || undefined,
      obergruppe: filters.obergruppe || undefined,
      hauptgruppe: filters.hauptgruppe || undefined,
      prefix: filters.prefix || undefined,
      kapitelScope: filters.kapitelScope || undefined,
    });
  }

  /**
   * Fetch sub-expenditure breakdown for drill-down
   */
  async getSubExpenditureBreakdown(filters: FilterState, parentType: string, titleId?: string): Promise<ExpenditureTypeData[]> {
    return this.fetch<ExpenditureTypeData[]>('/budget/sub-breakdown', {
      type: filters.type,
      parentType,
      ep: filters.einzelplan || undefined,
      kap: filters.kapitel || undefined,
      q: filters.query || undefined,
      // Forward a cross-parent spotlight sub-type (e.g. "Bildung") so the
      // breakdown stays scoped to it; the server only honors plain (non-compound)
      // values here — see /budget/sub-breakdown.
      subExpType: filters.subExpType || undefined,
      titleId: titleId || undefined,
      prefix: filters.prefix || undefined,
      kapitelScope: filters.kapitelScope || undefined,
      funktion: filters.funktion || undefined,
      oberfunktion: filters.oberfunktion || undefined,
      hauptfunktion: filters.hauptfunktion || undefined,
      gruppierung: filters.gruppierung || undefined,
      obergruppe: filters.obergruppe || undefined,
      hauptgruppe: filters.hauptgruppe || undefined,
    });
  }

  /**
   * Fetch title breakdown for third drill-down level (top 5 titles by ist values)
   */
  async getTitleBreakdown(filters: FilterState, parentType: string, subType?: string | null, titleId?: string, extraTitleIds?: string[]): Promise<TitleData[]> {
    // Guard against missing parentType (race condition protection)
    if (!parentType) {
      console.warn('getTitleBreakdown called without parentType, skipping');
      return [];
    }
    return this.fetch<TitleData[]>('/budget/title-breakdown', {
      type: filters.type,
      parentType,
      // Send empty string explicitly (don't convert to undefined) so server can filter for sub_expenditure_type = ''
      subType: subType !== null && subType !== undefined ? subType : undefined,
      ep: filters.einzelplan || undefined,
      kap: filters.kapitel || undefined,
      q: filters.query || undefined,
      titleId: titleId || undefined,
      prefix: filters.prefix || undefined,
      kapitelScope: filters.kapitelScope || undefined,
      funktion: filters.funktion || undefined,
      oberfunktion: filters.oberfunktion || undefined,
      hauptfunktion: filters.hauptfunktion || undefined,
      gruppierung: filters.gruppierung || undefined,
      obergruppe: filters.obergruppe || undefined,
      hauptgruppe: filters.hauptgruppe || undefined,
      // Ids the user broke out of Sonstiges — returned as their own series.
      extraTitleIds: extraTitleIds && extraTitleIds.length > 0 ? extraTitleIds.join(',') : undefined,
    });
  }

  /**
   * List the individual titles rolled into the chart's "Sonstiges" bucket
   * (everything outside the top 5) so the user can break them out. Ranked by
   * each title's largest single-year Ist peak (biggest single points first);
   * the server caps the count (largest kept).
   */
  async getSonstigesTitles(filters: FilterState, parentType: string, subType?: string | null): Promise<SonstigesTitle[]> {
    if (!parentType) {
      console.warn('getSonstigesTitles called without parentType, skipping');
      return [];
    }
    return this.fetch<SonstigesTitle[]>('/budget/sonstiges-titles', {
      type: filters.type,
      parentType,
      subType: subType !== null && subType !== undefined ? subType : undefined,
      ep: filters.einzelplan || undefined,
      kap: filters.kapitel || undefined,
      q: filters.query || undefined,
      prefix: filters.prefix || undefined,
      kapitelScope: filters.kapitelScope || undefined,
      funktion: filters.funktion || undefined,
      oberfunktion: filters.oberfunktion || undefined,
      hauptfunktion: filters.hauptfunktion || undefined,
      gruppierung: filters.gruppierung || undefined,
      obergruppe: filters.obergruppe || undefined,
      hauptgruppe: filters.hauptgruppe || undefined,
    });
  }

  /**
   * Fetch search suggestions
   */
  async getSearchSuggestions(query: string, budgetType: 'E' | 'A' = 'A'): Promise<SearchSuggestion[]> {
    if (!query || query.length < 2) {
      return [];
    }

    const response = await this.fetch<{ suggestions: SearchSuggestion[] }>('/suggestions', {
      q: query,
      type: budgetType,
    });

    return response.suggestions;
  }

  /**
   * Fetch filter options. The flat lists are scoped by `einzelplan` (when
   * set) for chart label lookups; the hierarchical trees are unscoped and
   * narrowed client-side via /filter-matrix.
   */
  async getFilterOptions(
    einzelplan?: string,
    budgetType: 'E' | 'A' = 'A',
  ): Promise<FilterOptions> {
    return this.fetch<FilterOptions>('/filter-options', {
      type: budgetType,
      ep: einzelplan,
    });
  }

  /**
   * Fetch the filter matrix: distinct combinations of all filter columns for
   * a budget type. Used for client-side cross-dimension faceting.
   */
  async getFilterMatrix(budgetType: 'E' | 'A' = 'A'): Promise<FilterMatrixResponse> {
    return this.fetch<FilterMatrixResponse>('/filter-matrix', { type: budgetType });
  }

  /**
   * Fetch GDP data for all years
   */
  async getGdpData(): Promise<{ year: number; value: number }[]> {
    return this.fetch<{ year: number; value: number }[]>('/gdp');
  }

  /**
   * Fetch unfiltered budget totals per year (excluding Haushaltstechnik & Sonderposten)
   */
  async getBudgetTotals(type: 'E' | 'A'): Promise<BudgetTotalData[]> {
    return this.fetch<BudgetTotalData[]>('/budget-totals', { type });
  }

  /**
   * Fetch the timestamp of the most recent budget data load (ISO string, or
   * null if no run is recorded). Powers the footer's "Letztes Update" line.
   */
  async getLastUpdated(): Promise<{ lastUpdated: string | null }> {
    return this.fetch<{ lastUpdated: string | null }>('/budget/last-updated');
  }

  /**
   * Fetch budget entries for table
   */
  async getBudgetEntries(
    filters: FilterState,
    pagination: { limit?: number; offset?: number } = {},
    year?: number
  ): Promise<BudgetEntriesResponse> {
    const { limit = 100, offset = 0 } = pagination;
    return this.fetch<BudgetEntriesResponse>('/budget/entries', {
      type: filters.type,
      ep: filters.einzelplan || undefined,
      kap: filters.kapitel || undefined,
      q: filters.query || undefined,
      expType: filters.expenditureType || undefined,
      subExpType: filters.subExpType || undefined,
      funktion: filters.funktion || undefined,
      oberfunktion: filters.oberfunktion || undefined,
      hauptfunktion: filters.hauptfunktion || undefined,
      gruppierung: filters.gruppierung || undefined,
      obergruppe: filters.obergruppe || undefined,
      hauptgruppe: filters.hauptgruppe || undefined,
      prefix: filters.prefix || undefined,
      kapitelScope: filters.kapitelScope || undefined,
      limit: limit.toString(),
      offset: offset.toString(),
      year: year?.toString(),
    });
  }

  /**
   * Fetch export data with full time series per title
   */
  async getExportData(filters: FilterState): Promise<ExportResponse> {
    return this.fetch<ExportResponse>('/budget/export', {
      type: filters.type,
      ep: filters.einzelplan || undefined,
      kap: filters.kapitel || undefined,
      q: filters.query || undefined,
      expType: filters.expenditureType || undefined,
      subExpType: filters.subExpType || undefined,
      funktion: filters.funktion || undefined,
      oberfunktion: filters.oberfunktion || undefined,
      hauptfunktion: filters.hauptfunktion || undefined,
      gruppierung: filters.gruppierung || undefined,
      obergruppe: filters.obergruppe || undefined,
      hauptgruppe: filters.hauptgruppe || undefined,
      prefix: filters.prefix || undefined,
      kapitelScope: filters.kapitelScope || undefined,
    });
  }

  /**
   * Fetch detailed information for a specific title
   */
  async getTitleDetails(titleId: string, type: 'E' | 'A' = 'A'): Promise<TitleDetailsResponse> {
    return this.fetch<TitleDetailsResponse>(`/budget/title-details/${encodeURIComponent(titleId)}`, {
      type,
    });
  }
}
